/**
 * Planting Wizard questions — mirrors handoff prototype Q[].
 */

export const WIZARD_ANSWER_KEYS = Object.freeze([
  'goal',
  'season',
  'sun',
  'equip',
  'budget',
]);

export const EMPTY_WIZARD_ANSWERS = Object.freeze({
  goal: '',
  season: '',
  sun: '',
  equip: '',
  budget: '',
});

export const WIZARD_QUESTIONS = Object.freeze([
  {
    key: 'goal',
    kicker: 'STEP 01 · THE JOB',
    stripLabel: 'THE JOB',
    title: 'What is this plot supposed to do?',
    blurb:
      'The blend changes a lot between a plot you hunt over in November and one that carries deer through the summer.',
    why: 'Draw plots lean on cereal grains that green up fast. Hold plots lean on perennials that survive browse.',
    options: [
      {
        id: 'draw',
        label: 'Draw deer in during season',
        note: 'Fast green-up, hunted over, October through January.',
      },
      {
        id: 'hold',
        label: 'Hold deer year-round',
        note: 'Perennial base, high tonnage, feeds through summer stress.',
      },
      {
        id: 'turkey',
        label: 'Turkey and small game',
        note: 'Bugging cover, seed heads, open walking room for poults.',
      },
      {
        id: 'soil',
        label: 'Build the soil back up',
        note: 'Cover crop rotation to fix nitrogen and break up hardpan.',
      },
    ],
  },
  {
    key: 'season',
    kicker: 'STEP 02 · THE SEASON',
    stripLabel: 'THE SEASON',
    title: 'When are you putting seed in the ground?',
    blurb:
      'Your zone window, not a calendar copied from somewhere else. Confirm zone and frost on Ground.',
    why: 'Planting outside the window is the single most common reason a plot fails, ahead of soil and fertilizer.',
    options: [
      {
        id: 'fall',
        label: 'This fall',
        note: 'Cool-season grains and clovers in your zone window.',
      },
      {
        id: 'spring',
        label: 'Next spring or summer',
        note: 'Warm-season legumes and grains after last frost in your zone.',
      },
      {
        id: 'year',
        label: 'Something that stays year-round',
        note: 'Perennial base, overseeded each fall.',
      },
    ],
  },
  {
    key: 'sun',
    kicker: 'STEP 03 · THE LIGHT',
    stripLabel: 'THE LIGHT',
    title: 'How much sun does the plot get?',
    blurb:
      'Walk it at noon. Count the hours of open sky, not the hours the sun is technically up.',
    why: 'Under four hours, clover and chicory outperform every grain — the grains stretch and fall over.',
    options: [
      {
        id: 'full',
        label: 'Full sun',
        note: 'Six hours or more of open sky.',
      },
      {
        id: 'part',
        label: 'Partial',
        note: 'Four to six hours. Timber on one or both sides.',
      },
      {
        id: 'shade',
        label: 'Shady',
        note: 'Under four hours. A logging road or a hole in the canopy.',
      },
    ],
  },
  {
    key: 'equip',
    kicker: 'STEP 04 · THE GEAR',
    stripLabel: 'THE GEAR',
    title: 'What are you working the ground with?',
    blurb:
      'Seed size and seeding rate both depend on how well you can get soil contact.',
    why: 'Hand-broadcast plots get a 25% higher seeding rate and need small seed that can germinate on the surface.',
    options: [
      {
        id: 'tractor',
        label: 'Tractor and disk',
        note: 'Full tillage, cultipacker, precise depth.',
      },
      {
        id: 'atv',
        label: 'ATV with a drag or harrow',
        note: 'Light tillage. Good enough for most blends.',
      },
      {
        id: 'hand',
        label: 'Hand broadcast only',
        note: 'Throw and pray. Timed to rain, small seed only.',
      },
    ],
  },
  {
    key: 'budget',
    kicker: 'STEP 05 · THE MONEY',
    stripLabel: 'THE MONEY',
    title: 'What are you willing to spend per acre?',
    blurb: 'Lime is the cheapest yield you can buy and the first thing people skip.',
    why: 'Under $50 an acre we drop the fertilizer rate and lime only the worst half of the plot.',
    options: [
      {
        id: 'low',
        label: 'Under $50 an acre',
        note: 'Seed only. Lime the worst spots.',
      },
      {
        id: 'mid',
        label: '$50 to $100 an acre',
        note: 'Seed, starter fertilizer, partial lime.',
      },
      {
        id: 'high',
        label: 'Over $100 an acre',
        note: 'Do it right. Full lime and fertilizer.',
      },
    ],
  },
]);

export function normalizeWizardIndex(index) {
  const n = Number(index);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.floor(n), WIZARD_QUESTIONS.length - 1);
}

export function countWizardAnswers(answers = {}) {
  return WIZARD_ANSWER_KEYS.filter((key) => Boolean(answers?.[key])).length;
}

export function isWizardComplete(answers = {}) {
  return countWizardAnswers(answers) === WIZARD_ANSWER_KEYS.length;
}

export function currentWizardQuestion(index, place = null) {
  const q = WIZARD_QUESTIONS[normalizeWizardIndex(index)];
  if (q.key !== 'season') return q;
  const copy = wizardSeasonCopy(place);
  return {
    ...q,
    blurb: copy.blurb,
    options: q.options.map((opt) => {
      if (opt.id === 'fall') return { ...opt, note: copy.fallNote };
      if (opt.id === 'spring') return { ...opt, note: copy.springNote };
      return opt;
    }),
  };
}

/** Season option notes from Ground frost/zone — never a hardcoded Sep 15 – Oct 20. */
export function wizardSeasonCopy(place = null) {
  const zone = String(place?.zone || '').trim();
  const first = String(place?.first || '').trim();
  const last = String(place?.last || '').trim();
  const zoneBit = zone ? `zone ${zone}` : 'your zone';

  let blurb = 'Your zone window, not a calendar copied from somewhere else. Confirm zone and frost on Ground.';
  if (zone && first && last) {
    blurb = `Zone ${zone}: fall before first frost around ${first}, spring after last frost around ${last}.`;
  } else if (zone && first) {
    blurb = `Zone ${zone}: fall before first frost around ${first}. Spring waits until after last frost.`;
  } else if (zone && last) {
    blurb = `Zone ${zone}: spring after last frost around ${last}. Fall is cool-season before first frost.`;
  } else if (zone) {
    blurb = `Zone ${zone} sets the window. Fall is cool-season; spring waits until after last frost. Confirm frost on Ground for dates.`;
  }

  return {
    blurb,
    fallNote: first
      ? `Cool-season grains and clovers. Be in the ground well before first frost around ${first}.`
      : `Cool-season grains and clovers in ${zoneBit} fall window.`,
    springNote: last
      ? `Warm-season legumes and grains after last frost around ${last}.`
      : `Warm-season legumes and grains after last frost in ${zoneBit}.`,
  };
}

/** CTA copy for the sticky bar (prototype). */
export function wizardContinueLabel(answers = {}, index = 0) {
  const q = currentWizardQuestion(index);
  const answered = Boolean(answers?.[q.key]);
  if (!answered) return 'PICK ONE';
  if (normalizeWizardIndex(index) < WIZARD_QUESTIONS.length - 1) return 'NEXT QUESTION';
  return 'READ THE GROUND';
}

export function canAdvanceWizard(answers = {}, index = 0) {
  const q = currentWizardQuestion(index);
  return Boolean(answers?.[q.key]);
}

const WIZARD_OPTION_IDS = Object.freeze(
  WIZARD_QUESTIONS.reduce((acc, question) => {
    acc[question.key] = new Set(question.options.map((option) => option.id));
    return acc;
  }, {})
);

/** Keep only known wizard keys/ids for save and hydrate. */
export function sanitizeWizardAnswers(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  WIZARD_ANSWER_KEYS.forEach((key) => {
    const value = String(source[key] || '').trim().toLowerCase();
    if (value && WIZARD_OPTION_IDS[key]?.has(value)) {
      out[key] = value;
    }
  });
  return out;
}

/**
 * Restore wizard draft from a saved group.
 * Falls back to planting_season when older plans have no wizard_answers.
 */
export function hydrateWizardAnswers(stored, plantingSeason = '') {
  const sanitized = sanitizeWizardAnswers(stored);
  const season = String(plantingSeason || '').trim().toLowerCase();
  if (!sanitized.season && (season === 'fall' || season === 'spring')) {
    sanitized.season = season;
  }
  return { ...EMPTY_WIZARD_ANSWERS, ...sanitized };
}

export function wizardIndexForHydratedAnswers(answers = {}) {
  return isWizardComplete(answers) ? WIZARD_QUESTIONS.length - 1 : 0;
}

/**
 * Apply an answer. Auto-advances to the next question when not on the last one.
 * @returns {{ answers: object, index: number }}
 */
export function applyWizardAnswer(answers, index, optionId) {
  const i = normalizeWizardIndex(index);
  const q = WIZARD_QUESTIONS[i];
  const nextAnswers = { ...EMPTY_WIZARD_ANSWERS, ...answers, [q.key]: optionId };
  const nextIndex = i < WIZARD_QUESTIONS.length - 1 ? i + 1 : i;
  return { answers: nextAnswers, index: nextIndex };
}

export function advanceWizardIndex(answers, index) {
  const i = normalizeWizardIndex(index);
  if (!canAdvanceWizard(answers, i)) return i;
  if (i < WIZARD_QUESTIONS.length - 1) return i + 1;
  return i;
}
